import { forwardRef, useEffect, useState } from 'react';
import { SwitchOff, SwitchOn } from '@sas/react-svg';
import cx from 'classnames';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { markEvent } from '../../marked-events';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { Icon } from '../Icon';
import bundle from './NovaComponents-Switch-gui-icu.properties';
import cn from './Switch.css.json';
import './Switch.css';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const Switch = /*#__PURE__*/forwardRef(function Switch({
  style,
  className,
  isOn,
  defaultOn = false,
  disabled = false,
  readOnly = false,
  tooltip = {},
  testId,
  onPress,
  ariaLabel,
  ariaDescribedBy = [],
  ariaLabelledBy
}, ref) {
  const [switchStatus, setSwitchStatus] = useState(defaultOn);
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const isControlled = isOn !== undefined;
  useEffect(() => {
    if (isControlled) {
      setSwitchStatus(isOn ?? false);
    }
  }, [isControlled, isOn]);
  const handleSize = '0.5rem';
  const dt = useDesignTokens();
  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const tokens = {
    ...renderInlineCssVars({
      ...(switchStatus ? {
        fg: dt.tokens['color-action-icon-on-off-selected'],
        bg: dt.tokens['color-action-background-on-off-selected'],
        bd: dt.tokens['color-action-border-on-off-selected'],
        'anim-tf': dt.tokens['ease-in']
      } : {
        fg: dt.tokens['color-action-icon-on-off-normal'],
        bg: dt.tokens['color-action-background-on-off-normal'],
        bd: dt.tokens['color-action-border-on-off-normal'],
        'anim-tf': dt.tokens['ease-out']
      }),
      'anim-dur': dt.tokens['duration-200'],
      'handle-size': handleSize,
      'outline-offset': '1px',
      ...(!isNewTokenValueFlagEnabled && {
        'outline-offset': 0
      })
    }, 'switch'),
    ...renderInlineCssVars(switchStatus ? {
      fg: dt.tokens['color-action-icon-on-off-selected-disabled'],
      bg: dt.tokens['color-action-background-on-off-selected-disabled'],
      bd: dt.tokens['color-action-border-on-off-selected-disabled']
    } : {
      fg: dt.tokens['color-action-icon-on-off-disabled'],
      bg: dt.tokens['color-action-background-on-off-disabled'],
      bd: dt.tokens['color-action-border-on-off-disabled']
    }, 'switch-disabled')
  };
  const onSwitchChange = (newState, event) => {
    if (!isControlled) {
      setSwitchStatus(newState);
    }
    onPress?.(newState, event);
  };
  return /*#__PURE__*/_jsx("div", {
    "data-testid": testId,
    style: {
      ...tokens,
      ...style
    },
    className: cx(cn.container, className),
    children: /*#__PURE__*/_jsx("button", {
      ref: ref,
      type: "button",
      "data-testid": postfixTestId(testId, 'button'),
      className: cx(cn.switchButton, disabled && cn.disabled, readOnly && cn.readonly),
      role: "switch",
      "aria-checked": switchStatus,
      "aria-disabled": disabled ? true : undefined,
      "aria-readonly": readOnly && !disabled ? true : undefined,
      "aria-label": ariaLabelledBy ? undefined : ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined,
      title: switchStatus ? tooltip.on ?? localize('on.tooltip.label') : tooltip.off ?? localize('off.tooltip.label'),
      tabIndex: 0,
      onMouseUp: evt => {
        if (disabled || readOnly) return;
        evt.preventDefault();
        onSwitchChange(!switchStatus, evt);
      },
      onKeyDown: evt => {
        if (evt.key === 'Enter') {
          evt.preventDefault();
          // Mark the event so Dialog doesn't submit a default action
          markEvent(evt);
        }
      },
      onKeyUp: evt => {
        if (disabled || readOnly) return;
        switch (evt.key) {
          case ' ':
          case 'Enter':
            evt.preventDefault();
            onSwitchChange(!switchStatus, evt);
            return;
          case 'ArrowRight':
            onSwitchChange(isRTL ? false : true, evt);
            return;
          case 'ArrowLeft':
            onSwitchChange(isRTL ? true : false, evt);
            return;
        }
      },
      children: /*#__PURE__*/_jsx("div", {
        className: cn.switchInner,
        style: {
          transform: `${switchStatus ? isRTL ? 'translate3d(calc(-100% - 1px + 1.5 * var(--switch-handle-size)), 0, 0)' : 'translate3d(calc(100% + 1px - 1.5 * var(--switch-handle-size)), 0, 0)' : 'translate3d(0, 0, 0)'}${isRTL ? ' rotateX(180deg)' : ''}`
        },
        children: /*#__PURE__*/_jsxs("div", {
          className: cn.handle,
          children: [/*#__PURE__*/_jsx(Icon, {
            icon: SwitchOn,
            className: cn.iconOn,
            theme: () => ({
              '--icon-default-override-fg': disabled ? 'var(--switch-disabled-fg)' : 'var(--switch-fg)'
            }),
            tooltip: "",
            ariaLabel: ""
          }), /*#__PURE__*/_jsx(Icon, {
            icon: SwitchOff,
            className: cn.iconOff,
            theme: () => ({
              '--icon-default-override-fg': disabled ? 'var(--switch-disabled-fg)' : 'var(--switch-fg)'
            }),
            tooltip: "",
            ariaLabel: ""
          })]
        })
      })
    })
  });
});
//# sourceMappingURL=Switch.js.map